import type { CrmPipeline, CrmPipelineSetting, CrmPipelineStep } from '@integrations/crm-pipelines';

import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal, untracked } from '@angular/core';
import { UpperCasePipe } from '@angular/common';
import { form, required } from '@angular/forms/signals';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TranslateModule } from '@ngx-translate/core';

export type SetPipelineEventPayload = {
    pipeline: CrmPipeline;
    step: CrmPipelineStep;
    settings: CrmPipelineSetting[];
    skipHistory?: boolean;
};

type PipelineSelection = {
    pipeline: CrmPipeline | null;
    step: CrmPipelineStep | null;
};

@Component({
    selector: 'web-crm-pipelines-collection',
    templateUrl: './web-crm-pipelines-collection.component.html',
    styleUrls: ['./web-crm-pipelines-collection.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [UpperCasePipe, TranslateModule, BsDropdownModule],
})
export class WebCrmPipelinesCollectionComponent {
    /** Currently selected pipeline/step; `null` when nothing is picked yet. */
    public readonly selectedPipeline = input.required<CrmPipeline | null>();
    public readonly selectedPipelineStep = input.required<CrmPipelineStep | null>();

    public readonly crmPipelines = input.required<CrmPipeline[]>();
    public readonly isLoading = input<boolean>(false);
    public readonly isError = input<boolean>(false);
    public readonly isCrmDisabled = input<boolean>(false);

    public readonly selectedPipelineChange = output<SetPipelineEventPayload>();
    public readonly setSkipHistoryEvent = output<void>();
    public readonly selectedPipelineNameChange = output<string>();

    /**
     * Internal signal form holding the current selection. The dropdown has no
     * native control to bind `[formField]` to, so the click handler writes the
     * form; the input → model effect covers external changes (undo/redo).
     */
    private readonly model = signal<PipelineSelection>({ pipeline: null, step: null });
    protected readonly pipelineForm = form(this.model, (path) => {
        required(path.pipeline);
    });

    protected readonly hasRequiredError = computed(() => this.pipelineForm.pipeline().invalid());

    /** One-shot guards mirroring the `take(1)` chains of the Observable era. */
    private autoSelectDone = false;
    private crmDisabledHandled = false;

    constructor() {
        // input → form (host value, undo/redo, external resets)
        effect(() => {
            const pipeline = this.selectedPipeline();
            const step = this.selectedPipelineStep();
            this.setModel(pipeline, step);
        });

        // Auto-select the first pipeline once the collection arrives and
        // nothing is selected yet — skipping undo history, as before.
        effect(() => {
            const pipelines = this.crmPipelines();
            const isCrmDisabled = this.isCrmDisabled();
            const selected = this.pipelineForm.pipeline().value();

            untracked(() => {
                if (this.autoSelectDone || !pipelines?.length || selected || isCrmDisabled) {
                    return;
                }

                this.autoSelectDone = true;
                const pipeline: CrmPipeline = pipelines[0];
                this.setSkipHistoryEvent.emit();
                this.setPipeline({ pipeline, step: pipeline.steps[0], settings: pipeline.settings, skipHistory: true });
            });
        });

        // Clear the selection once when CRM gets disabled.
        effect(() => {
            const isCrmDisabled = this.isCrmDisabled();

            untracked(() => {
                if (!isCrmDisabled || this.crmDisabledHandled) {
                    return;
                }

                this.crmDisabledHandled = true;
                this.setSkipHistoryEvent.emit();
                this.setPipeline({ pipeline: null, step: null, settings: null, skipHistory: true });
            });
        });

        // Silent rename sync when the pipeline was renamed in CRM.
        effect(() => {
            const pipelines = this.crmPipelines();
            const selected = this.pipelineForm.pipeline().value();

            untracked(() => {
                if (!pipelines?.length || !selected) {
                    return;
                }

                const pipeline = pipelines.find((crmPipeline) => crmPipeline.id === selected.id);

                if (pipeline && pipeline.name !== selected.name) {
                    this.selectedPipelineNameChange.emit(pipeline.name);
                }
            });
        });
    }

    public setPipeline(payload: SetPipelineEventPayload): void {
        this.setModel(payload.pipeline, payload.step);
        this.selectedPipelineChange.emit(payload);
    }

    private setModel(pipeline: CrmPipeline | null, step: CrmPipelineStep | null): void {
        this.model.set({
            pipeline: pipeline ? structuredClone(pipeline) : null,
            step: step ? structuredClone(step) : null,
        });
    }

    public get pipelineName(): string {
        return this.pipelineForm.pipeline().value()?.name ?? 'choose_pipeline';
    }

    public get pipelineNameFirstLetter(): string {
        return this.pipelineName.charAt(0);
    }

    public get pipelineStepName(): string {
        return this.pipelineForm.step().value()?.name ?? 'choose_status';
    }
}
